import type Fuse from "fuse.js";
import { z } from "zod";

import {
    type FundExplorerApiData,
    type FundExplorerDataIndexed,
    type TopLevelDataObj,
} from "@hsl/fund-explorer/types";

import {
    type FEError,
    type FEMessage,
    type FundExplorerSearch,
} from "../../store/types";
import { type SearchConfig } from "../../types";

export const FundFinderRestrictionSchema = z.object({
    jurisdictions: z.array(z.number()).optional(),
    audiences: z.array(z.number()).optional(),
});

export const FundExplorerTypeSchema = z.enum([
    "collective",
    "etf",
    "pmc",
    "mps",
]);
export type FundExplorerType = z.infer<typeof FundExplorerTypeSchema>;

export const FundFinderConfigSchema = z.object({
    type: FundExplorerTypeSchema,
    id: z.number(),
    url: z.string(),
    exclude: FundFinderRestrictionSchema.optional(),
    include: FundFinderRestrictionSchema.optional(),
});
export type FundFinderConfig = z.infer<typeof FundFinderConfigSchema>;

export const FundFinderConfigJSONSchema = z.object({
    fundCentres: z.array(FundFinderConfigSchema),
});
export type FundFinderConfigJSON = z.infer<typeof FundFinderConfigJSONSchema>;

export type StoreConfig = {
    baseurl: string;
    id: string;
    language: string;
    audience: string;
    type: FundExplorerType;
    explorerURL: string;
};

export type RawData = {
    type: FundExplorerType;
    explorerURL: string;
} & FundExplorerApiData;

export type DataWithType = {
    type: FundExplorerType;
    data: TopLevelDataObj;
    url: string;
};

export type SearchResults = Record<string, Fuse.FuseResultMatch[]>;
export type FundFinderSearch = Omit<FundExplorerSearch, "results"> & {
    searchOn: string[];
    results: SearchResults | undefined;
};
export interface FundFinderStore {
    baseurl: string;
    config: FundFinderConfig[];
    rawData: RawData[];
    indexedData:
        | {
              type: FundExplorerType;
              data: FundExplorerDataIndexed;
              url: string;
          }[]
        | null;
    errors: FEError[] | [];
    messages: FEMessage[] | [];
    loading: boolean;
    search: FundFinderSearch;
    translations: Record<string, string>;
    init: (configs: StoreConfig[]) => void | Promise<void>;
    getDataById: <T = TopLevelDataObj>(id: string) => T | undefined;
    getDataByIdWithType: (id: string) => DataWithType | undefined;
    applySearch: (term: string) => void;
    fetchRawData: (config: StoreConfig) => Promise<void>;
    indexData: () => void;
    createSearch: (config?: SearchConfig) => void;
    setTranslations: (translations: Record<string, string>) => void;
    getAbsoluteURL: (relativeURL: string, root?: string) => string;
    getExplorerURL: (type: FundExplorerType) => string | undefined;
}
